import React, { useState } from 'react';
import { EmptyState } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  MdUpdate, MdOpenInNew, MdBadge, MdEmail, MdPhone, MdPersonOff,
  MdLocationOn, MdGroup, MdCalendarToday, MdFingerprint, MdOutlineUpdate,
} from 'react-icons/md';
import { FaCheckCircle, FaTimesCircle, FaBuilding } from 'react-icons/fa';
import { formatDate } from 'helpers/utils';
import { getInitials, getAvatarColor } from 'components/common/select/EmployeeBadge';
import ManagerLink from 'components/app/organization/employee/detail/ManagerLink';
import {
  Val, InfoRow, SectionPanel, Card, Toggle, GenderRow,
} from 'components/app/organization/employee/detail/employeeInfoPrimitives';
import UserDetailUpdateEmployeeModal from './UserDetailUpdateEmployeeModal';

/**
 * The Employee tab of the User detail screen.
 *
 * Shows the same record as the employee detail screen, so it renders through the
 * same primitives — previously it was flat label/value rows with *disabled*
 * radios and checkboxes, which look like controls you are forbidden to use rather
 * than like information.
 */
const UserDetailEmployeeMaping = ({ employee }) => {
  const [showModal, setShowModal] = useState(false);

  if (!employee) {
    return (
      <>
        <EmptyState
          icon={MdPersonOff}
          title="No employee record linked"
          description="This account is not mapped to an employee yet. Create the profile to pull in department, site office and HR information."
          action={() => setShowModal(true)}
          actionLabel="Create Employee"
        />
        <UserDetailUpdateEmployeeModal showModal={showModal} setShowModal={setShowModal} user={null} />
      </>
    );
  }

  const hr = employee.hr || {};
  // Only the fields that come from the HR system itself. `hr.employee_code` is
  // just a mirror of hr_code and `hr.worker` is a boolean that is present even
  // when nothing was synced, so neither proves an integration exists.
  const hasHrIntegration = ['timekeeping_code', 'direct_manager', 'team_name', 'contract_type_name', 'working_location_code', 'nationality']
    .some((key) => hr[key]);
  const employeePath = `/organization/employees/${employee.id}`;

  return (
    <>
      <Card>
        {/* ── Identity strip ────────────────────────────────────────────────── */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-4 tw-border-b tw-border-solid tw-border-slate-100">
          <div className="tw-flex tw-items-center tw-gap-4 tw-min-w-0">
            <div
              className="tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-text-white tw-font-bold tw-text-lg tw-select-none"
              style={{ width: 52, height: 52, background: getAvatarColor(employee.name), boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
            >
              {getInitials(employee.name)}
            </div>
            <div className="tw-min-w-0">
              <h5 className="tw-text-base tw-font-semibold tw-text-slate-800 tw-m-0 tw-mb-1 tw-truncate">
                {employee.full_name || employee.name}
                <span className="tw-text-slate-300 tw-font-normal"> · </span>
                <span className="tw-text-primary-600">#{employee.id}</span>
              </h5>
              <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                {employee.active ? (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-emerald-50 tw-text-emerald-700 tw-text-xs tw-font-semibold">
                    <FaCheckCircle size={10} /> Activated
                  </span>
                ) : (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-red-50 tw-text-red-600 tw-text-xs tw-font-semibold">
                    <FaTimesCircle size={10} /> Not active
                  </span>
                )}
                {employee.title && <span className="tw-text-xs tw-text-slate-400">{employee.title}</span>}
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
            {/* New tab: cross-checking the HR record should not cost you the
                account screen you are reading. */}
            <a
              href={employeePath}
              target="_blank"
              rel="noopener noreferrer"
              title="Open this employee record in a new tab"
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-bg-slate-50 hover:tw-text-primary-600 tw-transition-colors"
              style={{ textDecoration: 'none' }}
            >
              <MdBadge size={13} /> Employee record <MdOpenInNew size={11} className="tw-opacity-60" />
            </a>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-semibold tw-rounded-md tw-bg-primary-600 hover:tw-bg-primary-700 tw-text-white tw-cursor-pointer tw-border-0 tw-transition-colors"
            >
              <MdUpdate size={14} /> Update
            </button>
          </div>
        </div>

        {/* ── Basic information ─────────────────────────────────────────────── */}
        <div className="tw-px-5 tw-py-4 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-8">
          <div className="tw-divide-y tw-divide-slate-50">
            <InfoRow label="Name">
              <Link
                to={employeePath}
                target="_blank"
                rel="noopener noreferrer"
                title="Open the employee record in a new tab"
                className="tw-text-sm tw-font-medium tw-text-primary-600 hover:tw-underline tw-inline-flex tw-items-center tw-gap-1"
                style={{ textDecoration: 'none' }}
              >
                {employee.name || 'N/A'}
                <MdOpenInNew size={11} className="tw-opacity-60" />
              </Link>
            </InfoRow>
            <InfoRow label="Full name"><Val>{employee.full_name}</Val></InfoRow>
            <InfoRow label="Title"><Val>{employee.title}</Val></InfoRow>
            <InfoRow label="Email">
              {employee.email ? (
                <a href={`mailto:${employee.email}`} className="tw-text-sm tw-font-medium tw-text-primary-600 hover:tw-underline tw-flex tw-items-center tw-gap-1" style={{ textDecoration: 'none' }}>
                  <MdEmail size={13} />{employee.email}
                </a>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Gender"><GenderRow gender={employee.gender} /></InfoRow>
          </div>

          <div className="tw-divide-y tw-divide-slate-50">
            <InfoRow label="Phone">
              {employee.phone_number ? (
                <a href={`tel:${employee.phone_number}`} className="tw-text-sm tw-font-medium tw-text-primary-600 hover:tw-underline tw-flex tw-items-center tw-gap-1" style={{ textDecoration: 'none' }}>
                  <MdPhone size={13} />{employee.phone_number}
                </a>
              ) : <Val />}
            </InfoRow>
            {/* Manager lives here only — it used to be repeated in the
                Organization block below, from two different payload fields. */}
            <InfoRow label="Manager">
              <ManagerLink manager={employee.manager || employee.parent} />
            </InfoRow>
            <InfoRow label="Sales team">
              <Toggle checked={!!employee.is_sales} label={employee.is_sales ? 'Member' : 'Not member'} />
            </InfoRow>
            <InfoRow label="Active">
              <Toggle checked={!!employee.active} label={employee.active ? 'Active' : 'Inactive'} />
            </InfoRow>
          </div>
        </div>
      </Card>

      {/* ── Details ─────────────────────────────────────────────────────────── */}
      {/* No margin override here: `tw-mb-0` alongside the Card's own `tw-mb-4`
          would be decided by CSS source order, not by class order. */}
      <Card>
        <div className="tw-px-5 tw-py-3.5 tw-border-b tw-border-solid tw-border-slate-100 tw-bg-slate-50/60">
          <h5 className="tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-m-0">Details</h5>
        </div>

        <div className={`tw-px-5 tw-py-5 tw-grid tw-grid-cols-1 tw-gap-5 ${hasHrIntegration ? 'lg:tw-grid-cols-3' : 'md:tw-grid-cols-2'}`}>
          <SectionPanel
            icon={<FaBuilding size={12} />}
            title="Organization"
            accentColor="tw-border-l-sky-400"
            headerBg="tw-bg-sky-50"
            iconColor="tw-text-sky-500"
          >
            <InfoRow label="Department">
              {employee.department?.name ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-xl tw-bg-sky-50 tw-text-sky-700 tw-text-xs tw-font-semibold tw-border tw-border-solid tw-border-sky-100">
                  {employee.department.name}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Site office">
              {employee.site?.name ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-sky-600">
                  <MdLocationOn size={13} className="tw-shrink-0" />{employee.site.name}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Position group"><Val>{employee.position_group?.name}</Val></InfoRow>
          </SectionPanel>

          <SectionPanel
            icon={<MdBadge size={14} />}
            title="HR"
            accentColor="tw-border-l-primary-400"
            headerBg="tw-bg-primary-50"
            iconColor="tw-text-primary-500"
          >
            <InfoRow label="Start date">
              {employee.start_date ? (
                <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-primary-600">
                  <MdCalendarToday size={12} className="tw-shrink-0" />{formatDate(employee.start_date)}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="HR code">
              {employee.hr_code ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-primary-50 tw-text-primary-700 tw-text-xs tw-font-bold tw-font-mono tw-border tw-border-solid tw-border-primary-100">
                  <MdFingerprint size={11} />{employee.hr_code}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Birthday"><Val>{formatDate(employee.birthday) || employee.birthday}</Val></InfoRow>
            <InfoRow label="Staff policy">
              {employee.staff_policy ? (
                <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-violet-50 tw-text-violet-700 tw-text-xs tw-font-semibold tw-border tw-border-solid tw-border-violet-100">
                  {employee.staff_policy === 'SALES' ? 'Sales-Office' : employee.staff_policy}
                </span>
              ) : <Val />}
            </InfoRow>
            <InfoRow label="Updated at">
              <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-slate-500">
                <MdOutlineUpdate size={13} className="tw-text-primary-400" />
                {formatDate(employee.updated_at) || 'N/A'}
              </span>
            </InfoRow>
          </SectionPanel>

          {hasHrIntegration && (
            <SectionPanel
              icon={<MdGroup size={14} />}
              title="HR Integration"
              accentColor="tw-border-l-violet-400"
              headerBg="tw-bg-violet-50"
              iconColor="tw-text-violet-500"
            >
              <InfoRow label="Employee code">
                {hr.employee_code ? (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-violet-50 tw-text-violet-700 tw-text-xs tw-font-bold tw-font-mono tw-border tw-border-solid tw-border-violet-100">
                    {hr.employee_code}
                  </span>
                ) : <Val />}
              </InfoRow>
              <InfoRow label="Timekeeping code"><Val>{hr.timekeeping_code}</Val></InfoRow>
              <InfoRow label="Direct manager">
                {hr.direct_manager ? (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-slate-100 tw-text-slate-700 tw-text-xs tw-font-semibold">
                    {hr.direct_manager}
                  </span>
                ) : <Val />}
              </InfoRow>
              <InfoRow label="Team name"><Val>{hr.team_name}</Val></InfoRow>
              <InfoRow label="Contract type"><Val>{hr.contract_type_name}</Val></InfoRow>
              <InfoRow label="Work location">
                {hr.working_location_code ? (
                  <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-violet-600">
                    <MdLocationOn size={13} className="tw-shrink-0" />{hr.working_location_code}
                  </span>
                ) : <Val />}
              </InfoRow>
              <InfoRow label="Nationality"><Val>{hr.nationality}</Val></InfoRow>
            </SectionPanel>
          )}
        </div>
      </Card>

      <UserDetailUpdateEmployeeModal showModal={showModal} setShowModal={setShowModal} user={employee} />
    </>
  );
};

UserDetailEmployeeMaping.propTypes = {
  employee: PropTypes.object,
};

export default UserDetailEmployeeMaping;
